import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import './Register.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8009/api';

// ── PLANS ─────────────────────────────────────────────────────────────────────
const PLANS = {
  starter:      { name: 'Starter',      desc: 'Up to 5 users · 2 warehouses · 2 devices',  price: { usd: 29,  eur: 27,  tzs: 67000,  ugx: 108000 } },
  business:     { name: 'Business',     desc: 'Up to 15 users · 5 warehouses · 5 devices', price: { usd: 79,  eur: 75,  tzs: 182000, ugx: 295000 }, popular: true },
  professional: { name: 'Professional', desc: 'Unlimited users & warehouses · 10 devices', price: { usd: 199, eur: 189, tzs: 460000, ugx: 745000 } },
};

const CURRENCIES = [
  { key: 'eur', label: '€ EUR', flag: '🇪🇺' },
  { key: 'usd', label: '$ USD', flag: '🇺🇸' },
  { key: 'tzs', label: 'TSh',   flag: '🇹🇿' },
  { key: 'ugx', label: 'UGX',   flag: '🇺🇬' },
];

const COUNTRIES = [
  { code: 'IT',    label: 'Italy' },
  { code: 'TZ',    label: 'Tanzania' },
  { code: 'UG',    label: 'Uganda' },
  { code: 'KE',    label: 'Kenya' },
  { code: 'RW',    label: 'Rwanda' },
  { code: 'ET',    label: 'Ethiopia' },
  { code: 'NG',    label: 'Nigeria' },
  { code: 'GH',    label: 'Ghana' },
  { code: 'ZA',    label: 'South Africa' },
  { code: 'US',    label: 'United States' },
  { code: 'GB',    label: 'United Kingdom' },
  { code: 'DE',    label: 'Germany' },
  { code: 'FR',    label: 'France' },
  { code: 'ES',    label: 'Spain' },
  { code: 'OTHER', label: 'Other' },
];

// ── PHONE VALIDATION PER COUNTRY ──────────────────────────────────────────────
const phoneValidation = {
  IT:    { pattern: /^(\+39)?[\s]?([0-9]{2,4})[\s]?([0-9]{6,8})$/,                         format: '+39 XXX XXXXXXX' },
  TZ:    { pattern: /^(\+255)?[\s]?([0-9]{9,10})$/,                                         format: '+255 XXXXXXXXX' },
  UG:    { pattern: /^(\+256)?[\s]?([0-9]{9,10})$/,                                         format: '+256 XXXXXXXXX' },
  KE:    { pattern: /^(\+254)?[\s]?([0-9]{9,10})$/,                                         format: '+254 XXXXXXXXX' },
  RW:    { pattern: /^(\+250)?[\s]?([0-9]{9,10})$/,                                         format: '+250 XXXXXXXXX' },
  ET:    { pattern: /^(\+251)?[\s]?([0-9]{9,10})$/,                                         format: '+251 XXXXXXXXX' },
  NG:    { pattern: /^(\+234)?[\s]?([0-9]{10,11})$/,                                        format: '+234 XXXXXXXXXX' },
  GH:    { pattern: /^(\+233)?[\s]?([0-9]{9,10})$/,                                         format: '+233 XXXXXXXXX' },
  ZA:    { pattern: /^(\+27)?[\s]?([0-9]{9,10})$/,                                          format: '+27 XXXXXXXXX' },
  US:    { pattern: /^(\+1)?[\s]?(\([0-9]{3}\)|[0-9]{3})[\s\-]?[0-9]{3}[\s\-]?[0-9]{4}$/, format: '+1 (XXX) XXX-XXXX' },
  GB:    { pattern: /^(\+44)?[\s]?([0-9]{10,11})$/,                                         format: '+44 XXXXXXXXXXX' },
  DE:    { pattern: /^(\+49)?[\s]?([0-9]{10,12})$/,                                         format: '+49 XXXXXXXXXXX' },
  FR:    { pattern: /^(\+33)?[\s]?([0-9]{9,10})$/,                                          format: '+33 X XX XX XX XX' },
  ES:    { pattern: /^(\+34)?[\s]?([0-9]{9})$/,                                             format: '+34 XXX XXX XXX' },
  OTHER: { pattern: /^[\+]?[0-9\s\-\(\)]{8,20}$/,                                           format: 'International format' },
};

function validatePhone(phone, countryCode) {
  if (!phone || !countryCode) return null;
  const v = phoneValidation[countryCode] || phoneValidation.OTHER;
  return v.pattern.test(phone);
}

function validateAddress(address, countryCode) {
  if (!address || !countryCode) return null;
  const trimmed = address.trim();
  if (trimmed.length < 10) return false;
  switch (countryCode) {
    case 'IT': return /via|piazza|corso|viale|[0-9]{5}/i.test(trimmed);
    case 'US': return /[0-9]+.*[0-9]{5}/.test(trimmed);
    case 'GB': return /[A-Z]{1,2}[0-9]{1,2}\s?[0-9][A-Z]{2}/i.test(trimmed);
    case 'TZ': case 'UG': case 'KE': case 'RW': case 'ET': case 'NG': case 'GH': case 'ZA':
      return trimmed.length >= 15;
    default: return trimmed.length >= 10;
  }
}

function formatPrice(price, currency) {
  const syms = { usd: '$', eur: '€', tzs: 'TSh ', ugx: 'UGX ' };
  if (currency === 'tzs' || currency === 'ugx')
    return `${syms[currency]}${price >= 1000 ? (price / 1000).toFixed(0) + 'K' : price}`;
  return `${syms[currency]}${price}`;
}

// ── AUTO LOGIN ────────────────────────────────────────────────────────────────
async function autoLogin(username, password) {
  try {
    const loginRes = await fetch(`${API_URL}/auth/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!loginRes.ok) return false;

    const tokens = await loginRes.json();
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);

    const userRes = await fetch(`${API_URL}/users/me/`, {
      headers: { 'Authorization': `Bearer ${tokens.access}`, 'Content-Type': 'application/json' },
    });
    if (!userRes.ok) return false;
    const user = await userRes.json();

    try {
      const subRes = await fetch(`${API_URL}/subscriptions/status/`, {
        headers: { 'Authorization': `Bearer ${tokens.access}`, 'Content-Type': 'application/json' },
      });
      if (subRes.ok) user.subscription = await subRes.json();
    } catch (_) {}

    localStorage.setItem('user', JSON.stringify(user));
    return true;
  } catch (_) {
    return false;
  }
}

// ── ERROR MODAL ───────────────────────────────────────────────────────────────
function ErrorModal({ isOpen, title, message, onClose }) {
  if (!isOpen) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface, #131c28)',
        border: '1px solid var(--border-2, rgba(255,255,255,0.12))',
        borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)', position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '14px', right: '14px',
          background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
          width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
        }}><X size={16} /></button>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>❌</div>
        <h3 style={{ color: 'var(--text, #eaf0f8)', fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>
          {title}
        </h3>
        <p style={{ color: 'var(--text-2, #8b97a8)', fontSize: '14px', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
          {message}
        </p>
        <button onClick={onClose} style={{
          marginTop: '24px', width: '100%', padding: '12px',
          background: 'var(--accent, #00d4aa)', color: '#000',
          border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
        }}>OK</button>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function Register() {
  const navigate = useNavigate();
  const [step, setStep]                 = useState(1);
  const [theme, setTheme]               = useState(() => localStorage.getItem('theme') || 'dark');
  const [currency, setCurrency]         = useState('eur');
  const [selectedPlan, setSelectedPlan] = useState('business');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [modal, setModal]               = useState({ open: false, title: '', message: '' });
  const [phoneValid, setPhoneValid]     = useState(null);
  const [addressValid, setAddressValid] = useState(null);

  // contact_phone matches Django field name exactly
  const [org, setOrg] = useState({
    name: '',
    contact_email: '',
    country: '',
    contact_phone: '',
    address: '',
    business_type: '',
  });

  const [admin, setAdmin] = useState({
    first_name: '', last_name: '', username: '', password: '', confirm_password: '',
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // ── ORG UPDATE + real-time validation ─────────────────────────────────────
  function updateOrg(field, value) {
    setOrg(prev => {
      const next = { ...prev, [field]: value };

      if (field === 'contact_phone' || field === 'country') {
        const p = field === 'contact_phone' ? value : next.contact_phone;
        const c = field === 'country' ? value : next.country;
        setPhoneValid(p ? validatePhone(p, c) : null);
      }

      if (field === 'address' || field === 'country') {
        const a = field === 'address' ? value : next.address;
        const c = field === 'country' ? value : next.country;
        setAddressValid(a ? validateAddress(a, c) : null);
      }

      return next;
    });
  }

  function updateAdmin(field, value) { setAdmin(prev => ({ ...prev, [field]: value })); }

  function showError(title, message) { setModal({ open: true, title, message }); }
  function closeModal() { setModal({ open: false, title: '', message: '' }); }

  // ── VALIDATION ────────────────────────────────────────────────────────────
  function validateStep1() {
    if (!org.name.trim())           return 'Organization name is required';
    if (!org.contact_email.trim())  return 'Business email is required';
    if (!org.country)               return 'Country is required';
    if (!org.contact_phone.trim()) {
      return 'Phone number is required';
    } else if (validatePhone(org.contact_phone, org.country) === false) {
      const fmt = phoneValidation[org.country]?.format || 'International format';
      return `Invalid phone format. Expected: ${fmt}`;
    }
    if (org.address && validateAddress(org.address, org.country) === false) {
      return 'Please enter a valid address for the selected country';
    }
    if (!org.business_type) return 'Business type is required';
    return null;
  }

  function validateStep2() {
    if (!admin.first_name.trim() || !admin.last_name.trim()) return 'Full name is required';
    if (!admin.username.trim())    return 'Username is required';
    if (admin.password.length < 8) return 'Password must be at least 8 characters';
    if (admin.password !== admin.confirm_password) return 'Passwords do not match';
    return null;
  }

  function handleStep1() {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
  }

  // ── SUBMIT ────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    const err = validateStep2();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization: { ...org, plan: selectedPlan },
          user: {
            first_name: admin.first_name,
            last_name:  admin.last_name,
            username:   admin.username,
            email:      org.contact_email,
            password:   admin.password,
          },
          plan:     selectedPlan,
          currency: currency,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const msg = Object.entries(data.errors)
            .map(([field, messages]) => {
              const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const text  = Array.isArray(messages) ? messages.join(', ') : messages;
              return `${label}: ${text}`;
            })
            .join('\n\n');
          showError('Registration Failed', msg);
        } else {
          showError('Registration Failed', data.error || data.message || 'An unexpected error occurred.');
        }
        return;
      }

      // Auto-login → dashboard
      const loggedIn = await autoLogin(admin.username, admin.password);
      if (loggedIn) {
        sessionStorage.setItem('newRegistration', 'true');
        window.location.href = '/dashboard';
      } else {
        navigate('/login?registered=true');
      }

    } catch {
      showError('Network Error', 'Could not connect to the server.\nPlease check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const phoneFmt = org.country
    ? (phoneValidation[org.country]?.format || 'International format')
    : '+XX XXX XXX XXXX';

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="lp-register" data-theme={theme}>
      <div className="lp-register__bg-grid"></div>
      <div className="lp-register__bg-glow"></div>

      <button className="lp-register__theme-toggle"
        onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      <div className="lp-register__topnav" onClick={() => navigate('/')}>
        <span className="lp-register__topnav-logo">⬡</span>
        <span className="lp-register__topnav-name">TaalumaERP</span>
        <span className="lp-register__topnav-tag">Start free 14-day trial</span>
      </div>

      <div className="lp-register__progress">
        <div className={`lp-reg-step ${step >= 1 ? 'lp-reg-step--active' : ''} ${step > 1 ? 'lp-reg-step--done' : ''}`}>
          <div className="lp-reg-step__num">{step > 1 ? <Check size={16} /> : '1'}</div>
          <span className="lp-reg-step__label">Organization</span>
        </div>
        <div className={`lp-reg-step__line ${step > 1 ? 'lp-reg-step__line--done' : ''}`}>
          <div className="lp-reg-step__line-fill"></div>
        </div>
        <div className={`lp-reg-step ${step >= 2 ? 'lp-reg-step--active' : ''}`}>
          <div className="lp-reg-step__num">2</div>
          <span className="lp-reg-step__label">Admin Account</span>
        </div>
      </div>

      <div className="lp-register__card">

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <>
            <div className="lp-register__step-head">
              <h2>Tell us about your organization</h2>
              <p>This sets up your workspace. You can change details later.</p>
            </div>

            <div className="lp-reg-form">

              <div className="lp-reg-group">
                <label>Organization Name *</label>
                <input type="text" value={org.name}
                  onChange={e => updateOrg('name', e.target.value)}
                  placeholder="e.g. Ethnic World Import/Export" />
              </div>

              <div className="lp-reg-group">
                <label>Business Email *</label>
                <input type="email" value={org.contact_email}
                  onChange={e => updateOrg('contact_email', e.target.value)}
                  placeholder="info@yourcompany.com" />
              </div>

              <div className="lp-reg-row">
                <div className="lp-reg-group">
                  <label>Country *</label>
                  <select value={org.country} onChange={e => updateOrg('country', e.target.value)}>
                    <option value="">Select country</option>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>

                <div className="lp-reg-group">
                  <label>Phone Number *</label>
                  <input type="tel" value={org.contact_phone}
                    onChange={e => updateOrg('contact_phone', e.target.value)}
                    placeholder={phoneFmt}
                    style={{
                      borderColor:
                        phoneValid === true  ? '#10b981' :
                        phoneValid === false ? '#ef4444' : '',
                    }}
                  />
                  {phoneValid === true  && <small style={{ color: '#10b981' }}>✓ Valid phone number</small>}
                  {phoneValid === false && <small style={{ color: '#ef4444' }}>Expected: {phoneFmt}</small>}
                </div>
              </div>

              <div className="lp-reg-group">
                <label>Business Address</label>
                <textarea value={org.address}
                  onChange={e => updateOrg('address', e.target.value)}
                  placeholder={
                    org.country === 'IT' ? 'Via Zanella 130, 31015 Conegliano TV, Italy' :
                    org.country === 'US' ? '123 Main Street, New York, NY 10001' :
                    org.country === 'GB' ? '10 Downing Street, London SW1A 2AA' :
                    'Complete business address'
                  }
                  style={{
                    borderColor:
                      addressValid === true  ? '#10b981' :
                      addressValid === false ? '#ef4444' : '',
                  }}
                />
                {addressValid === true  && <small style={{ color: '#10b981' }}>✓ Valid address format</small>}
                {addressValid === false && <small style={{ color: '#ef4444' }}>Please enter a complete address</small>}
              </div>

              <div className="lp-reg-group">
                <label>Business Type *</label>
                <select value={org.business_type} onChange={e => updateOrg('business_type', e.target.value)}>
                  <option value="">Select business type</option>
                  {['Import/Export','Wholesale','Retail','Manufacturing','Distribution','Agriculture','Other'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Plan selector */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-2)', display: 'block', marginBottom: '10px' }}>
                  Choose Your Plan
                </label>
                <div className="lp-reg-currencies" style={{ marginBottom: '12px' }}>
                  {CURRENCIES.map(c => (
                    <button key={c.key} type="button"
                      className={`lp-reg-currency ${currency === c.key ? 'active' : ''}`}
                      onClick={() => setCurrency(c.key)}>
                      {c.flag} {c.label}
                    </button>
                  ))}
                </div>
                <div className="lp-reg-pricing">
                  {Object.entries(PLANS).map(([key, plan]) => (
                    <div key={key}
                      className={`lp-reg-plan ${selectedPlan === key ? 'lp-reg-plan--selected' : ''} ${plan.popular ? 'lp-reg-plan--popular' : ''}`}
                      onClick={() => setSelectedPlan(key)}>
                      <div className="lp-reg-plan__info">
                        <div className="lp-reg-plan__name">{plan.name}</div>
                        <div className="lp-reg-plan__desc">{plan.desc}</div>
                      </div>
                      <div className="lp-reg-plan__price">
                        <div className="lp-reg-plan__amount">{formatPrice(plan.price[currency], currency)}</div>
                        <div className="lp-reg-plan__period">/month</div>
                      </div>
                      <div className="lp-reg-plan__check">
                        {selectedPlan === key && <Check size={13} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lp-reg-trial">
                🎉 <span><strong>14-day free trial</strong> — no credit card required. Full access to all features.</span>
              </div>

              {error && <div className="lp-reg-error">⚠️ {error}</div>}

              <div className="lp-reg-actions">
                <button type="button" className="lp-reg-btn-secondary" onClick={() => navigate('/')}>Cancel</button>
                <button type="button" className="lp-reg-btn-primary" onClick={handleStep1}>
                  Continue <ArrowRight size={16} />
                </button>
              </div>

            </div>
          </>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="lp-register__step-head">
              <h2>Create your admin account</h2>
              <p>This will be the owner account with full access.</p>
            </div>

            <div className="lp-reg-form">

              <div className="lp-reg-row">
                <div className="lp-reg-group">
                  <label>First Name *</label>
                  <input type="text" value={admin.first_name}
                    onChange={e => updateAdmin('first_name', e.target.value)}
                    placeholder="First name" />
                </div>
                <div className="lp-reg-group">
                  <label>Last Name *</label>
                  <input type="text" value={admin.last_name}
                    onChange={e => updateAdmin('last_name', e.target.value)}
                    placeholder="Last name" />
                </div>
              </div>

              <div className="lp-reg-group">
                <label>Username *</label>
                <input type="text" value={admin.username}
                  onChange={e => updateAdmin('username', e.target.value)}
                  placeholder="Choose a username" autoComplete="username" />
                <small>Used to sign in. Cannot be changed later.</small>
              </div>

              <div className="lp-reg-group">
                <label>Email Address</label>
                <input type="email" value={org.contact_email} readOnly
                  style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                <small>Using the business email entered in Step 1.</small>
              </div>

              <div className="lp-reg-row">
                <div className="lp-reg-group">
                  <label>Password *</label>
                  <input type="password" value={admin.password}
                    onChange={e => updateAdmin('password', e.target.value)}
                    placeholder="Min. 8 characters" autoComplete="new-password" />
                </div>
                <div className="lp-reg-group">
                  <label>Confirm Password *</label>
                  <input type="password" value={admin.confirm_password}
                    onChange={e => updateAdmin('confirm_password', e.target.value)}
                    placeholder="Repeat password" autoComplete="new-password"
                    style={{
                      borderColor:
                        admin.confirm_password && admin.password === admin.confirm_password ? '#10b981' :
                        admin.confirm_password && admin.password !== admin.confirm_password ? '#ef4444' : '',
                    }}
                  />
                  {admin.confirm_password && admin.password === admin.confirm_password &&
                    <small style={{ color: '#10b981' }}>✓ Passwords match</small>}
                  {admin.confirm_password && admin.password !== admin.confirm_password &&
                    <small style={{ color: '#ef4444' }}>Passwords do not match</small>}
                </div>
              </div>

              <div className="lp-reg-info">
                ℹ️ Your organization <strong>{org.name}</strong> will be on the <strong>{PLANS[selectedPlan].name}</strong> plan
                — {formatPrice(PLANS[selectedPlan].price[currency], currency)}/month after your 14-day trial.
              </div>

              {loading ? (
                <div className="lp-reg-loading">
                  <div className="lp-reg-spinner"></div>
                  <p>Creating your account...</p>
                  <small>This may take a moment</small>
                </div>
              ) : (
                <>
                  {error && <div className="lp-reg-error">⚠️ {error}</div>}
                  <div className="lp-reg-actions">
                    <button type="button" className="lp-reg-btn-secondary"
                      onClick={() => { setStep(1); setError(''); }}>
                      ← Back
                    </button>
                    <button type="submit" className="lp-reg-btn-primary">
                      Start Free Trial <ArrowRight size={16} />
                    </button>
                  </div>
                </>
              )}

            </div>
          </form>
        )}

      </div>

      <div className="lp-register__footer">
        Already have an account? <a href="/login">Sign in</a>
      </div>

      <ErrorModal
        isOpen={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={closeModal}
      />
    </div>
  );
}